import { useState } from 'react';
import {
  Search,
  ChevronDown,
  ChevronRight,
  Inbox,
  User,
  UserX,
  CheckCircle,
  AlertTriangle,
  X,
  Filter,
  Calendar,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import type { Severity, PlatformStatus } from '../../data/types';

// ============================================================================
// TYPES
// ============================================================================

export type QuickFilter = 'all_open' | 'my_tickets' | 'unassigned' | 'closed' | 'all';

interface IncidentSidebarProps {
  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;
  // Quick filters
  activeQuickFilter: QuickFilter;
  onQuickFilterChange: (filter: QuickFilter) => void;
  // Detailed filters
  selectedSeverities: Severity[];
  onSeverityChange: (severities: Severity[]) => void;
  selectedStatuses: PlatformStatus[];
  onStatusChange: (statuses: PlatformStatus[]) => void;
  selectedTenants: string[];
  onTenantChange: (tenants: string[]) => void;
  // Date range
  dateFrom?: string;
  dateTo?: string;
  onDateFromChange?: (date: string) => void;
  onDateToChange?: (date: string) => void;
  // Auto-resolved exclusion (AB#133)
  hideAutoResolved?: boolean;
  onHideAutoResolvedChange?: (value: boolean) => void;
  // Options
  tenants: Array<{ tenantId: string; displayName: string }>;
  currentUserId?: string;
  // Counts for badges
  counts?: {
    allOpen?: number;
    myTickets?: number;
    unassigned?: number;
    closed?: number;
  };
  /**
   * Atomic reset. When provided, "Clear" calls this instead of firing the
   * individual onChange callbacks one by one (the list page uses it to reset
   * the URL and its remembered view in one step).
   */
  onClearAll?: () => void;
  className?: string;
}

// ============================================================================
// FILTER OPTIONS
// ============================================================================

const severityOptions: { value: Severity; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-500' },
  { value: 'low', label: 'Low', color: 'bg-blue-500' },
  { value: 'informational', label: 'Informational', color: 'bg-slate-400' },
];

const statusOptions: { value: PlatformStatus; label: string; color: string }[] = [
  { value: 'new', label: 'New', color: 'bg-amber-500' },
  { value: 'acknowledged', label: 'Acknowledged', color: 'bg-blue-500' },
  { value: 'investigating', label: 'Investigating', color: 'bg-purple-500' },
  { value: 'closed', label: 'Closed', color: 'bg-green-500' },
  { value: 'reopened', label: 'Reopened', color: 'bg-red-500' },
];

// ============================================================================
// COLLAPSIBLE SECTION
// ============================================================================

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: number;
}

function FilterSection({ title, children, defaultOpen = true, badge }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-border-default last:border-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-text-secondary hover:bg-bg-tertiary/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          {title}
          {badge !== undefined && badge > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">
              {badge}
            </span>
          )}
        </span>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-text-muted" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-muted" />
        )}
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function IncidentSidebar({
  searchQuery,
  onSearchChange,
  activeQuickFilter,
  onQuickFilterChange,
  selectedSeverities,
  onSeverityChange,
  selectedStatuses,
  onStatusChange,
  selectedTenants,
  onTenantChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  hideAutoResolved = false,
  onHideAutoResolvedChange,
  tenants,
  counts,
  onClearAll,
  className,
}: IncidentSidebarProps) {
  // Everything that narrows the list. The search term counts too — it was the
  // one filter the badge ignored, which is how a search silently followed an
  // analyst onto "All Open" (AB#137).
  const activeFilterCount =
    (searchQuery ? 1 : 0) +
    selectedSeverities.length +
    selectedStatuses.length +
    selectedTenants.length +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0) +
    (hideAutoResolved ? 1 : 0);

  const clearAllFilters = () => {
    if (onClearAll) {
      onClearAll();
      return;
    }
    onSeverityChange([]);
    onStatusChange([]);
    onTenantChange([]);
    onSearchChange('');
    onDateFromChange?.('');
    onDateToChange?.('');
    onHideAutoResolvedChange?.(false);
    onQuickFilterChange('all_open');
  };

  const toggleSeverity = (severity: Severity) => {
    if (selectedSeverities.includes(severity)) {
      onSeverityChange(selectedSeverities.filter((s) => s !== severity));
    } else {
      onSeverityChange([...selectedSeverities, severity]);
    }
    onQuickFilterChange('all');
  };

  const toggleStatus = (status: PlatformStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusChange(selectedStatuses.filter((s) => s !== status));
    } else {
      onStatusChange([...selectedStatuses, status]);
    }
    onQuickFilterChange('all');
  };

  const toggleTenant = (tenantId: string) => {
    if (selectedTenants.includes(tenantId)) {
      onTenantChange(selectedTenants.filter((t) => t !== tenantId));
    } else {
      onTenantChange([...selectedTenants, tenantId]);
    }
  };

  // Quick filter presets
  const quickFilters: {
    id: QuickFilter;
    label: string;
    icon: React.ReactNode;
    count?: number;
  }[] = [
    {
      id: 'all_open',
      label: 'All Open',
      icon: <Inbox className="w-4 h-4" />,
      count: counts?.allOpen,
    },
    {
      id: 'my_tickets',
      label: 'My Tickets',
      icon: <User className="w-4 h-4" />,
      count: counts?.myTickets,
    },
    {
      id: 'unassigned',
      label: 'Unassigned',
      icon: <UserX className="w-4 h-4" />,
      count: counts?.unassigned,
    },
    {
      id: 'closed',
      label: 'Closed',
      icon: <CheckCircle className="w-4 h-4" />,
      count: counts?.closed,
    },
  ];

  return (
    <div
      className={cn(
        'w-64 flex-shrink-0 bg-bg-primary border-r border-border-default flex flex-col h-full',
        className
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-border-default">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </h2>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-7 px-2 text-xs">
              <X className="w-3 h-3 mr-1" />
              Clear ({activeFilterCount})
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search incidents..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={cn(
              'w-full pl-9 pr-3 py-2 text-sm rounded-lg',
              'bg-bg-tertiary border border-border-default',
              'text-text-primary placeholder:text-text-muted',
              'focus:outline-none focus:ring-2 focus:ring-brand-blue/50 focus:border-brand-blue/50'
            )}
          />
        </div>
      </div>

      {/* Quick Filters */}
      <div className="p-2 border-b border-border-default">
        {quickFilters.map((filter) => (
          <button
            key={filter.id}
            type="button"
            onClick={() => onQuickFilterChange(filter.id)}
            className={cn(
              'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
              activeQuickFilter === filter.id
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                : 'text-text-secondary hover:bg-bg-tertiary'
            )}
          >
            <span className="flex items-center gap-2">
              {filter.icon}
              {filter.label}
            </span>
            {filter.count !== undefined && (
              <span
                className={cn(
                  'px-2 py-0.5 text-xs font-medium rounded-full',
                  activeQuickFilter === filter.id
                    ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200'
                    : 'bg-bg-tertiary text-text-secondary'
                )}
              >
                {filter.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Detailed Filters */}
      <div className="flex-1 overflow-y-auto">
        {/* Severity */}
        <FilterSection title="Severity" badge={selectedSeverities.length}>
          <div className="space-y-1">
            {severityOptions.map(({ value, label, color }) => (
              <label
                key={value}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors',
                  selectedSeverities.includes(value)
                    ? 'bg-bg-tertiary'
                    : 'hover:bg-bg-tertiary/50'
                )}
              >
                <input
                  type="checkbox"
                  checked={selectedSeverities.includes(value)}
                  onChange={() => toggleSeverity(value)}
                  className="rounded border-border-default text-brand-blue focus:ring-brand-blue"
                />
                <span className={cn('w-2 h-2 rounded-full', color)} />
                <span className="text-sm text-text-secondary">{label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* Status */}
        <FilterSection
          title="Status"
          badge={selectedStatuses.length + (hideAutoResolved ? 1 : 0)}
          defaultOpen={false}
        >
          {onHideAutoResolvedChange && (
            <label
              className={cn(
                'flex items-start gap-2 px-2 py-1.5 mb-2 rounded cursor-pointer transition-colors border border-border-default',
                hideAutoResolved ? 'bg-bg-tertiary' : 'hover:bg-bg-tertiary/50'
              )}
            >
              <input
                type="checkbox"
                checked={hideAutoResolved}
                onChange={(e) => onHideAutoResolvedChange(e.target.checked)}
                className="mt-0.5 rounded border-border-default text-brand-blue focus:ring-brand-blue"
              />
              <span className="flex flex-col">
                <span className="text-sm text-text-secondary">Hide auto-resolved</span>
                <span className="text-xs text-text-muted">
                  Closed by Defender before an analyst touched them
                </span>
              </span>
            </label>
          )}
          <div className="space-y-1">
            {statusOptions.map(({ value, label, color }) => (
              <label
                key={value}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors',
                  selectedStatuses.includes(value)
                    ? 'bg-bg-tertiary'
                    : 'hover:bg-bg-tertiary/50'
                )}
              >
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(value)}
                  onChange={() => toggleStatus(value)}
                  className="rounded border-border-default text-brand-blue focus:ring-brand-blue"
                />
                <span className={cn('w-2 h-2 rounded-full', color)} />
                <span className="text-sm text-text-secondary">{label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* Tenant */}
        {tenants.length > 0 && (
          <FilterSection title="Tenant" badge={selectedTenants.length} defaultOpen={false}>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {tenants.map((tenant) => (
                <label
                  key={tenant.tenantId}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors',
                    selectedTenants.includes(tenant.tenantId)
                      ? 'bg-bg-tertiary'
                      : 'hover:bg-bg-tertiary/50'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selectedTenants.includes(tenant.tenantId)}
                    onChange={() => toggleTenant(tenant.tenantId)}
                    className="rounded border-border-default text-brand-blue focus:ring-brand-blue"
                  />
                  <span className="text-sm text-text-secondary truncate">
                    {tenant.displayName}
                  </span>
                </label>
              ))}
            </div>
          </FilterSection>
        )}

        {/* Date Range */}
        <FilterSection
          title="Date Range"
          badge={dateFrom || dateTo ? 1 : 0}
          defaultOpen={false}
        >
          <div className="space-y-3">
            <div>
              <label htmlFor="filter-date-from" className="block text-xs text-text-secondary mb-1">From</label>
              <div className="relative">
                <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  id="filter-date-from"
                  type="date"
                  title="Filter from date"
                  value={dateFrom || ''}
                  onChange={(e) => onDateFromChange?.(e.target.value)}
                  className={cn(
                    'w-full pl-8 pr-2 py-1.5 text-sm rounded',
                    'bg-bg-tertiary border border-border-default',
                    'text-text-primary',
                    'focus:outline-none focus:ring-2 focus:ring-brand-blue/50'
                  )}
                />
              </div>
            </div>
            <div>
              <label htmlFor="filter-date-to" className="block text-xs text-text-secondary mb-1">To</label>
              <div className="relative">
                <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  id="filter-date-to"
                  type="date"
                  title="Filter to date"
                  value={dateTo || ''}
                  onChange={(e) => onDateToChange?.(e.target.value)}
                  className={cn(
                    'w-full pl-8 pr-2 py-1.5 text-sm rounded',
                    'bg-bg-tertiary border border-border-default',
                    'text-text-primary',
                    'focus:outline-none focus:ring-2 focus:ring-brand-blue/50'
                  )}
                />
              </div>
            </div>
          </div>
        </FilterSection>
      </div>

      {/* Footer with severity warning summary */}
      <div className="p-3 border-t border-border-default bg-bg-tertiary/50">
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          <span>Critical incidents require immediate attention</span>
        </div>
      </div>
    </div>
  );
}
